import { Badge, Group, Tooltip } from '@mantine/core';
import { AlertTriangle } from 'lucide-react';
import { useFeatureFlag } from '../../../../stores/featureFlags';
import { SESSION_CONTENT_STATUS_CONFIG } from '../../../../config/statusConfig';
import type { SessionContentStatus } from '../../../../types';
import { getSessionContentBadge } from './sessionsHelpers';

interface SessionContentStatusBadgeProps {
  contentStatus: SessionContentStatus | null | undefined;
}

const BADGE_LABEL_STYLES = {
  label: { fontSize: 10, letterSpacing: '0.3px', fontWeight: 600 },
} as const;

/**
 * MED-1075 / E1.3k — left-side content-authoring badge for a session row on the
 * CM Sessions tab. Surfaces states defined elsewhere (agenda completeness
 * e1.3d, participant pre-session work e1.3f, over-length e1.3j).
 *
 * Renders nothing unless ALL of the following hold:
 *   - the `live_session_delivery` flag is on (AC1) — fail-closed while loading,
 *   - the session is group-coaching with content started (server returns a
 *     non-null content_status only for group coaching — AC9 — and
 *     getSessionContentBadge drops the not_started state — AC5).
 *
 * The primary badge (In progress / Complete) is text + color with no leading
 * icon; "Review" is an additive amber chip with a warning icon, shown alongside
 * the primary badge and never on its own (AC6, AC11). Each badge carries the
 * spec tooltip on hover (AC7).
 */
export function SessionContentStatusBadge({
  contentStatus,
}: SessionContentStatusBadgeProps) {
  // Hooks run unconditionally; the flag gates rendering below (AC1).
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');
  const badge = getSessionContentBadge(contentStatus);

  if (!liveSessionDeliveryEnabled || !badge) return null;

  const primary = SESSION_CONTENT_STATUS_CONFIG[badge.primaryKey];
  const review = SESSION_CONTENT_STATUS_CONFIG.review;

  return (
    <Group gap={6} wrap="nowrap">
      <Tooltip label={badge.primaryTooltip} withArrow multiline w={260} position="top">
        <Badge
          size="sm"
          color={primary.color}
          variant={primary.variant ?? 'light'}
          tt="uppercase"
          styles={BADGE_LABEL_STYLES}
        >
          {primary.label}
        </Badge>
      </Tooltip>
      {badge.showReview && (
        <Tooltip label={badge.reviewTooltip} withArrow multiline w={260} position="top">
          <Badge
            size="sm"
            color={review.color}
            variant={review.variant ?? 'light'}
            tt="uppercase"
            leftSection={<AlertTriangle size={10} />}
            styles={BADGE_LABEL_STYLES}
          >
            {review.label}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}
